/**
 * Обзор.
 *
 * Сверху — то, ради чего систему открывают: сколько заработали, какая
 * маржа, сколько должны. Ниже — состояние данных: без него цифры выше
 * нельзя интерпретировать, и прятать это в служебный раздел нельзя.
 */

import { Icon } from '../components/Icon'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../api'
import type { CurrentUser, DashboardData, ReportResult } from '../types'
import { Card, Empty, KpiSkeleton, Notice, Stat, Badge } from '../components/ui'
import { BarRows, ColumnChart } from '../finance/charts'
import { Delta } from '../finance/parts'
import { humanForbidden } from '../finance/data'
import { monthLabel, monthsOf, shortMonth } from '../finance/period'
import '../finance/finance.css'
import {
  deltaPercent,
  endOfCurrentMonth,
  formatRub,
  formatDate,
  formatDateTime,
  formatMonth,
  formatNumber,
  formatPercent,
  formatRelative,
  monthsAgo,
  pluralize,
} from '../format'

export const STREAM_TITLES: Record<string, string> = {
  sales: 'Продажи',
  purchases: 'Закупки',
  cashflow: 'Движение денег',
  settlements: 'Взаиморасчёты',
  stock_balance: 'Остатки',
  stock_moves: 'Движения товаров',
  gl_turnover: 'Обороты по счетам',
  dictionaries: 'Справочники',
  budget: 'Бюджеты',
  payroll: 'ФОТ',
  excel_gross_profit: 'Excel: валовая прибыль',
}

/**
 * Что показать на главной тому, у кого нет доступа к продажам.
 *
 * Логисту незачем сообщение о том, что маржа ему не назначена: у него есть
 * свои цифры, и главная должна открываться ими. Порядок — по убыванию
 * значимости: берётся первый отчёт, к которому у человека есть доступ.
 *
 * Это не универсальный конструктор дашбордов, а именованный список: какой
 * показатель главный и в каком разрезе его смотреть — решение содержательное,
 * и выводить его автоматически из описания отчёта значит угадывать.
 */
interface FeaturedBlock {
  code: string
  /** Разрез для графика. */
  groupBy: string
  /** Показатель, который рисуем. */
  metric: string
  chartTitle: string
  chartHint: string
  /** Плитки сверху: ключ показателя и подпись. */
  stats: Array<{
    key: string
    label: string
    hint?: string
    /** Штуки и рубли форматируются по-разному, и путать их нельзя. */
    kind?: 'money' | 'number' | 'percent'
    /** Рост этого показателя — плохая новость. */
    bad?: boolean
  }>
  /** Ограничивать ли период последними 12 месяцами. */
  periodic: boolean
  filters?: Record<string, string[]>
  /** Куда смотреть подробнее. */
  more?: { path: string; title: string }
}

const FEATURED: FeaturedBlock[] = [
  {
    code: 'stock_current',
    groupBy: 'warehouse',
    metric: 'amount',
    chartTitle: 'Запасы по складам',
    chartHint: 'Стоимость остатков на последнюю дату',
    // Количество не суммируется: штуки, листы и метры — разные единицы.
    // Итог в штуках по всем складам выглядел бы как показатель и не значил ничего.
    // Сервер по умолчанию отдаёт только товарные склады: основные средства,
    // брак и товары в пути в запас не входят (вид склада — правило в админке).
    stats: [{ key: 'amount', label: 'Стоимость запасов', hint: 'Товарные склады, на последнюю дату снимка' }],
    periodic: false,
    more: { path: '/stock/stock', title: 'Возраст запасов и неликвиды — раздел «Склад»' },
  },
  {
    code: 'receivables',
    groupBy: 'counterparty',
    metric: 'amount',
    chartTitle: 'Дебиторская задолженность по контрагентам',
    chartHint: 'Крупнейшие должники на последнюю дату',
    // Только дебиторская: без стороны долга сумма складывает долги клиентов
    // перед нами с нашими долгами поставщикам и не значит ничего.
    filters: { kind: ['Дебиторская'] },
    stats: [
      { key: 'amount', label: 'Дебиторская задолженность' },
      { key: 'not_overdue', label: 'Не просрочено' },
      { key: 'overdue_90_plus', label: 'Просрочка свыше 90 дней', hint: 'Самая проблемная часть', bad: true },
    ],
    periodic: false,
  },
  {
    code: 'cashflow',
    groupBy: 'cashflow_item',
    metric: 'net_flow',
    chartTitle: 'Движение денег по статьям',
    chartHint: 'За последние 12 месяцев',
    stats: [
      { key: 'inflow', label: 'Поступления' },
      { key: 'outflow', label: 'Списания' },
      { key: 'net_flow', label: 'Чистый поток' },
    ],
    periodic: true,
  },
]

export function Dashboard({ user }: { user: CurrentUser }) {
  const [data, setData] = useState<DashboardData | null>(null)
  const [byBranch, setByBranch] = useState<ReportResult | null>(null)
  const [byMonth, setByMonth] = useState<ReportResult | null>(null)
  const [featured, setFeatured] = useState<{ block: FeaturedBlock; result: ReportResult } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const hasMargin = user.reports.some((report) => report.code === 'margin_by_department')

  // Первый доступный из запасных. Считается один раз: список прав
  // за время жизни страницы не меняется.
  const fallback = hasMargin
    ? null
    : (FEATURED.find((item) => user.reports.some((report) => report.code === item.code && !report.unavailable_reason)) ?? null)

  const [salesError, setSalesError] = useState<string | null>(null)
  const [salesWindow, setSalesWindow] = useState<{ from: string; to: string } | null>(null)
  const [lastSales, setLastSales] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const dashboard = await api.dashboard()
        if (!cancelled) setData(dashboard)
      } catch (caught) {
        if (!cancelled) setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить сводку')
        return
      }

      // Блок отчёта падает отдельно от страницы: актуальность данных и журнал
      // загрузок нужны как раз тогда, когда с отчётом что-то не так.
      try {
        if (!hasMargin) {
          if (!fallback) return

          // Без limit: итоги сервер считает по выданным строкам, и с limit: 12
          // «Долг всего» был бы долгом двенадцати контрагентов.
          const result = await api.runReport(fallback.code, {
            group_by: [fallback.groupBy],
            metrics: [...new Set([...fallback.stats.map((stat) => stat.key), fallback.metric])],
            ...(fallback.periodic ? { period: { from: monthsAgo(11), to: endOfCurrentMonth() } } : {}),
            ...(fallback.filters ? { filters: fallback.filters } : {}),
            order_by: { key: fallback.metric, direction: 'desc' },
          })

          if (!cancelled) setFeatured({ block: fallback, result })
          return
        }

        // «12 месяцев» — двенадцать месяцев, которыми кончаются загруженные
        // продажи, а не календарь от сегодняшнего дня. Иначе при отставании
        // выгрузки в окно попадают пустые месяцы, а «к прошлому году»
        // показывает падение, которого не было.
        const history = await api.runReport('margin_by_department', {
          period: { from: monthsAgo(35), to: endOfCurrentMonth() },
          group_by: ['period'],
          metrics: ['revenue'],
        })
        const filled = history.rows
          .filter((row) => Number(row.revenue ?? 0) > 0)
          .map((row) => String(row.period).slice(0, 10))
          .sort()
        // Окно кончается последним закончившимся месяцем: текущий в 1С не закрыт,
        // и «год к году» сравнивал бы неполный октябрь с полным прошлогодним.
        const lastClosed = monthsAgo(1)
        const lastFilled = filled[filled.length - 1] ?? lastClosed
        const last = lastFilled < lastClosed ? lastFilled : lastClosed
        if (!cancelled) setLastSales(filled[filled.length - 1] ?? null)
        const lastDate = new Date(`${last}T00:00:00Z`)
        const period = {
          from: new Date(Date.UTC(lastDate.getUTCFullYear(), lastDate.getUTCMonth() - 11, 1)).toISOString().slice(0, 10),
          to: new Date(Date.UTC(lastDate.getUTCFullYear(), lastDate.getUTCMonth() + 1, 0)).toISOString().slice(0, 10),
        }
        if (!cancelled) setSalesWindow(period)

        const [branches, months] = await Promise.all([
          // Сравнение с прошлым годом, а не с прошлыми 12 месяцами:
          // у торговли выражена сезонность, и «год к году» — единственное
          // сравнение, которое что-то значит.
          api.runReport('margin_by_department', {
            period,
            group_by: ['branch'],
            compare: 'previous_year',
          }),
          api.runReport('margin_by_department', { period, group_by: ['period'] }),
        ])

        if (!cancelled) {
          setByBranch(branches)
          setByMonth(months)
        }
      } catch (caught) {
        if (!cancelled) {
          // Отказ по срезу прав — словами человека, а не администратора («нельзя ограничить по измерению»).
          const code = hasMargin ? 'margin_by_department' : (fallback?.code ?? '')
          setSalesError(
            caught instanceof ApiError
              ? caught.isForbidden
                ? humanForbidden(code, caught.message)
                : caught.message
              : 'Сервер не отвечает — проверьте связь и обновите страницу.',
          )
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [hasMargin, fallback])

  if (error) return <Notice tone="bad">{error}</Notice>
  if (!data) return <DashboardSkeleton />

  const totals = byBranch?.totals ?? {}
  const previous = byBranch?.comparison?.totals ?? {}
  const delta = (key: string) => deltaPercent(totals[key], previous[key])
  const staleRuns = data.recent_runs.filter((run) => run.is_stale)
  const failedRuns = data.recent_runs.filter((run) => run.status === 'failed')
  const windowLabel = salesWindow ? `${monthLabel(salesWindow.from)} — ${monthLabel(`${salesWindow.to.slice(0, 7)}-01`)}` : ''

  // Все месяцы окна, включая пустые: пропущенный месяц виден пустым
  // столбцом, а не схлопывается, сдвигая подписи.
  const monthlyByKey = new Map((byMonth?.rows ?? []).map((row) => [String(row.period).slice(0, 10), Number(row.revenue ?? 0)]))
  const windowMonths = salesWindow ? monthsOf(salesWindow) : []
  const branchRows = [...(byBranch?.rows ?? [])].sort((a, b) => Number(b.revenue ?? 0) - Number(a.revenue ?? 0))
  const previousByBranch = byBranch?.comparison?.rows ?? {}

  return (
    <div className="stack" style={{ gap: 20 }}>
      {(failedRuns.length > 0 || data.failed_checks.length > 0) && (
        <Notice tone="bad">
          <strong>Данные могли не обновиться.</strong>{' '}
          {data.failed_checks.length > 0
            ? `Сверка не сошлась по ${pluralize(data.failed_checks.length, 'показателю', 'показателям', 'показателям')} — витрины не обновлялись, показаны прежние цифры.`
            : `Загрузок с ошибкой: ${failedRuns.length}.`}{' '}
          {(user.is_admin || user.can_monitor_ingest) && <Link to="/data">Разобраться</Link>}
        </Notice>
      )}

      {staleRuns.length > 0 && (
        <Notice tone="warn">
          Есть загрузки, зависшие дольше часа ({staleRuns.length}). Скорее всего, процесс прервался.
        </Notice>
      )}

      {salesError && (
        <Notice tone="warn">
          <strong>Отчёт для сводки не получен.</strong> {salesError} Остальная страница актуальна.
        </Notice>
      )}

      {hasMargin && !byBranch && !salesError ? (
        <DashboardSkeleton blocksOnly />
      ) : hasMargin && byBranch ? (
        <>
          <div className="grid grid--kpi">
            <Stat
              label="Выручка за 12 месяцев"
              value={formatRub(totals.revenue)}
              delta={delta('revenue')}
              deltaLabel="к прошлому году"
              hint={windowLabel}
            />
            <Stat
              label="Валовая прибыль"
              value={formatRub(totals.gross_profit)}
              delta={delta('gross_profit')}
              deltaLabel="к прошлому году"
              hint="Только по строкам с себестоимостью"
            />
            <Stat
              label="Рентабельность"
              value={formatPercent(totals.margin_percent)}
              delta={delta('margin_percent')}
              deltaLabel="к прошлому году"
              hint="От выручки с известной себестоимостью"
            />
            <Stat
              label="Выручка без себестоимости"
              value={formatRub(totals.revenue_without_cost)}
              delta={delta('revenue_without_cost')}
              deltaLabel="к прошлому году"
              // Рост здесь плохая новость: доля выручки, по которой
              // маржу посчитать нельзя, увеличилась.
              invertDelta
              hint={
                byBranch.meta.rows_without_cost
                  ? `${pluralize(byBranch.meta.rows_without_cost, 'строка', 'строки', 'строк')} — маржа по ним не считается`
                  : 'Все строки с себестоимостью'
              }
              tone={Number(totals.revenue_without_cost ?? 0) > 0 ? 'bad' : undefined}
            />
          </div>

          {byBranch.meta.cost_is_preliminary && (
            <Notice tone="warn">
              В выборку попали месяцы, не закрытые в 1С. Себестоимость по ним предварительная и после
              закрытия изменится — маржа за текущий период считается оценкой.
            </Notice>
          )}

          {byBranch.meta.period_applied.narrowed && (
            <Notice tone="info">
              Период сужен вашими правами: показаны данные с {byBranch.meta.period_applied.from} по{' '}
              {byBranch.meta.period_applied.to}.
            </Notice>
          )}

          <div className="grid grid--2">
            <Card title="Выручка по филиалам" subtitle={`Без НДС, ${windowLabel} · справа — к тем же месяцам прошлого года`}>
              {branchRows.length === 0 ? (
                <div className="empty small">Продаж за эти месяцы нет</div>
              ) : (
                <BarRows
                  ariaLabel="Выручка по филиалам"
                  series={[{ key: 'revenue', label: 'Выручка', color: 'var(--series-1)' }]}
                  rows={branchRows.map((row) => ({
                    key: String(row.branch_guid ?? row.branch ?? ''),
                    label: String(row.branch ?? 'Филиал не определён'),
                    values: [Number(row.revenue ?? 0)],
                    aside: <Delta value={deltaPercent(row.revenue, previousByBranch[String(row.branch_guid ?? '')]?.revenue)} />,
                  }))}
                />
              )}
            </Card>

            <Card title="Динамика выручки">
              <ColumnChart
                ariaLabel="Выручка по месяцам"
                height={190}
                series={[{ key: 'revenue', label: 'Выручка без НДС', color: 'var(--series-1)' }]}
                points={windowMonths.map((month) => ({
                  key: month,
                  label: shortMonth(month),
                  title: monthLabel(month),
                  values: [monthlyByKey.get(month) ?? null],
                }))}
              />
            </Card>
          </div>
        </>
      ) : featured ? (
        <>
          <div className="grid grid--kpi">
            {featured.block.stats.map((stat) => {
              const value = featured.result.totals[stat.key]
              return (
                <Stat
                  key={stat.key}
                  label={stat.label}
                  value={
                    stat.kind === 'number'
                      ? formatNumber(value)
                      : stat.kind === 'percent'
                        ? formatPercent(value)
                        : formatRub(value)
                  }
                  hint={stat.hint}
                  tone={stat.bad && Number(value ?? 0) > 0 ? 'bad' : undefined}
                />
              )
            })}
          </div>

          <Card
            title={featured.block.chartTitle}
            subtitle={`${featured.block.chartHint}${featured.result.rows.length > 12 ? ` · 12 крупнейших из ${featured.result.rows.length}` : ''}`}
            actions={featured.block.more ? <Link className="small" to={featured.block.more.path}>{featured.block.more.title} <Icon name="arrow-right" size={14} /></Link> : undefined}
          >
            {featured.result.rows.length === 0 ? (
              <div className="empty small">Данных пока нет</div>
            ) : (
              <BarRows
                ariaLabel={featured.block.chartTitle}
                series={[{ key: featured.block.metric, label: featured.block.chartTitle, color: 'var(--series-1)' }]}
                rows={[...featured.result.rows]
                  .sort((a, b) => Number(b[featured.block.metric] ?? 0) - Number(a[featured.block.metric] ?? 0))
                  .slice(0, 12)
                  .map((row, index) => ({
                    key: String(row[`${featured.block.groupBy}_guid`] ?? row[featured.block.groupBy] ?? index),
                    label: String(row[featured.block.groupBy] ?? '—'),
                    values: [Number(row[featured.block.metric] ?? 0)],
                  }))}
              />
            )}
          </Card>
        </>
      ) : (
        <Card title="Ваши отчёты">
          {user.reports.length === 0 ? (
            <Empty
              icon="reports"
              title="Отчёты пока не назначены"
              hint="Доступ выдаёт администратор системы. До этого показывать нечего."
            />
          ) : (
            // Не извинение, а список того, что человеку доступно: главная
            // должна открываться его работой, а не сообщением о чужой.
            <div className="grid grid--2">
              {user.reports.map((report) => (
                <Link key={report.code} to={`/reports/${report.code}`} className="report-tile">
                  <span className="strong">{report.title}</span>
                  <span className="tiny muted">{report.category}</span>
                </Link>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid--2">
        <Card title="Актуальность данных">
          {data.freshness.length === 0 ? (
            <Empty title="Данные ещё не загружались" hint="Как только 1С пришлёт первый пакет, здесь появится время выгрузки." />
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              {data.freshness.map((source) => (
                <div className="row" key={source.stream}>
                  <span style={{ flex: 1 }}>{STREAM_TITLES[source.stream] ?? source.stream}</span>
                  <span className="small secondary mono">{formatRelative(source.data_as_of)}</span>
                </div>
              ))}
              {lastSales && (
                <div className="row">
                  <span style={{ flex: 1 }}>Последний месяц с продажами</span>
                  <span className={`small mono${lastSales < monthsAgo(1) ? ' warning-text strong' : ' secondary'}`}>{monthLabel(lastSales)}</span>
                </div>
              )}
              <div className="tiny muted" style={{ marginTop: 4 }}>
                Показано время, на которое актуальны данные в 1С, а не время их получения.
                {lastSales && lastSales < monthsAgo(1) && ` Выгрузка свежая, но документов позже чем за ${monthLabel(lastSales)} в ней нет.`}
              </div>
            </div>
          )}
        </Card>

        <Card title="Последние загрузки">
          {data.recent_runs.length === 0 ? (
            <Empty title="Загрузок ещё не было" />
          ) : (
            <div className="stack" style={{ gap: 8 }}>
              {data.recent_runs.slice(0, 6).map((run) => (
                <div className="row" key={run.id} style={{ flexWrap: 'nowrap' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="truncate" style={{ display: 'block' }}>
                      {STREAM_TITLES[run.stream ?? ''] ?? run.job_name}
                    </span>
                    <span className="tiny muted">
                      {formatDateTime(run.started_at)}
                      {run.period ? ` · ${describeRunPeriod(run.period)}` : ''}
                      {run.rows_upserted !== null ? ` · ${formatNumber(run.rows_upserted)} стр.` : ''}
                    </span>
                  </span>
                  {run.status === 'success' && !run.is_stale ? (
                    <span className="small secondary">успешно</span>
                  ) : (
                    <Badge tone={run.status === 'failed' || run.is_stale ? 'bad' : 'warn'}>
                      {run.is_stale ? 'завис' : run.status === 'failed' ? 'ошибка' : 'идёт'}
                    </Badge>
                  )}
                </div>
              ))}
              {(user.is_admin || user.can_monitor_ingest) && (
                <Link to="/data" className="small" style={{ marginTop: 4 }}>
                  Все загрузки и сверка <Icon name="arrow-right" size={14} />
                </Link>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

/** Скелетон той же геометрии: плитки и два блока. */
function DashboardSkeleton({ blocksOnly = false }: { blocksOnly?: boolean }) {
  return (
    <div className="stack" style={{ gap: 20 }} aria-busy="true" aria-label="Загрузка">
      <div className="grid grid--kpi">
        {Array.from({ length: 4 }, (_, index) => (
          <KpiSkeleton key={index} index={index} static />
        ))}
      </div>
      <div className="grid grid--2">
        <div className="skeleton" style={{ height: 280, borderRadius: 'var(--radius-lg)' }} />
        <div className="skeleton" style={{ height: 280, borderRadius: 'var(--radius-lg)' }} />
      </div>
      {!blocksOnly && (
        <div className="grid grid--2">
          <div className="skeleton" style={{ height: 220, borderRadius: 'var(--radius-lg)' }} />
          <div className="skeleton" style={{ height: 220, borderRadius: 'var(--radius-lg)' }} />
        </div>
      )}
    </div>
  )
}

/** Период пакета прогона: «май 2026» для целого месяца, иначе даты. */
function describeRunPeriod(period: { from: string; to: string }): string {
  const [year, month] = period.from.split('-').map(Number) as [number, number]
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const wholeMonth = period.from.endsWith('-01') && period.to === `${period.from.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`
  if (wholeMonth) return formatMonth(period.from).toLowerCase()
  if (period.from === period.to) return formatDate(period.from)
  return `${formatDate(period.from)} — ${formatDate(period.to)}`
}
